import { useEffect, useRef } from 'react'
import { profileUrl } from '../api/tmdb'
import Filmstrip from './Filmstrip'
import { subscribeToGame, getGame } from '../utils/firebaseGame'
import { IconClapper } from './icons'

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

function formatTime(s) {
  const m = Math.floor(s / 60).toString().padStart(2, '0')
  const sec = (s % 60).toString().padStart(2, '0')
  return `${m}:${sec}`
}

/**
 * Phase D – personal results screen shown after an H2H player finishes.
 * Listens to Firestore and auto-advances when the opponent also finishes.
 *
 * Props:
 *   gameId                 – Firestore game document ID
 *   result                 – { hops, timeSeconds, chain, finishedFirst, opponentAlreadyFinished }
 *   opponentActor          – { name, profile_path } – shown grayed while waiting
 *   onBothFinished         – called with full game data when status → 'results'
 */
export default function H2HPersonalResults({
  gameId,
  result,
  opponentActor,
  onBothFinished,
}) {
  const advancedRef = useRef(false)

  // If opponent already finished when we arrive, fetch the full game doc and
  // auto-advance after a brief pause so the player can see their own stats.
  useEffect(() => {
    if (!result.opponentAlreadyFinished || !gameId) return
    let cancelled = false
    const t = setTimeout(async () => {
      if (cancelled || advancedRef.current) return
      try {
        const game = await getGame(gameId)
        if (!cancelled && !advancedRef.current) {
          advancedRef.current = true
          onBothFinished(game)
        }
      } catch {
        if (!cancelled && !advancedRef.current) {
          advancedRef.current = true
          onBothFinished(null)
        }
      }
    }, 1800)
    return () => { cancelled = true; clearTimeout(t) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // If we finished first, subscribe to Firestore and wait for status → 'results'
  useEffect(() => {
    if (result.opponentAlreadyFinished || !gameId) return

    const unsub = subscribeToGame(gameId, (game) => {
      if (game.status === 'results' && !advancedRef.current) {
        advancedRef.current = true
        onBothFinished(game)
      }
    })

    return () => unsub()
  }, [gameId]) // eslint-disable-line react-hooks/exhaustive-deps

  const waiting = !result.opponentAlreadyFinished

  return (
    <div className="h2h-pr">
      <h2 className="h2h-pr__heading">You finished! <IconClapper size={18} style={{ verticalAlign: 'middle' }} /></h2>

      <div className="h2h-pr__stats">
        <div className="h2h-pr__stat">
          <span className="h2h-pr__stat-value">{result.hops}</span>
          <span className="h2h-pr__stat-label">Hops</span>
        </div>
        <div className="h2h-pr__divider" aria-hidden="true">·</div>
        <div className="h2h-pr__stat">
          <span className="h2h-pr__stat-value">{formatTime(result.timeSeconds)}</span>
          <span className="h2h-pr__stat-label">Time</span>
        </div>
      </div>

      <div className="h2h-pr__filmstrip-wrap">
        <Filmstrip chain={result.chain} onItemClick={() => {}} />
      </div>

      {waiting ? (
        <div className="h2h-pr__waiting">
          {opponentActor?.profile_path && (
            <img
              className="h2h-pr__opponent-photo"
              src={profileUrl(opponentActor.profile_path, 'w185') || PLACEHOLDER}
              alt={opponentActor.name ?? 'Opponent'}
              onError={(e) => { e.target.src = PLACEHOLDER }}
            />
          )}
          <p className="h2h-pr__waiting-text">
            Waiting for opponent to finish…
          </p>
        </div>
      ) : (
        <p className="h2h-pr__advancing">Loading results…</p>
      )}
    </div>
  )
}
